import { DownloadIcon, ViewIcon } from '@chakra-ui/icons'
import {
  Flex,
  Icon,
  Image as ChakraImage,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  useDisclosure,
  Box,
} from '@chakra-ui/react'
import { BbButton } from 'Core/BbButton'
import { ElevatedButton } from 'Core/ElevatedButton'
import { useEffect } from 'react'
import { DropzoneInputProps, DropzoneRootProps } from 'react-dropzone'
import { BsFileEarmarkBinaryFill } from 'react-icons/bs'
import { MdCancel } from 'react-icons/md'

interface IFileUploadProps {
  uploadDesc?: string
  getRootProps: <T extends DropzoneRootProps>(props?: T | undefined) => T
  getInputProps: <T extends DropzoneInputProps>(props?: T | undefined) => T
  isLoading?: boolean
  file: File | null
  src?: string
  onClear?: () => void
  onlyCameraAllowed?: boolean
  full?: boolean
}

/**
 * @depends react-dropzone(https://www.npmjs.com/package/react-dropzone)
 * @param IFileUploadProps
 */
export const FileUpload: React.FC<IFileUploadProps> = (props: IFileUploadProps) => {
  const {
    uploadDesc = 'Drag and drop here or upload image/pdf',
    getRootProps,
    getInputProps,
    isLoading = false,
    file,
    src,
    onClear,
    onlyCameraAllowed = false,
    full = true,
  } = props

  const { isOpen, onOpen, onClose } = useDisclosure()
  const file_extension =
    src && src.split('&file_extension=') && src.split('&file_extension=')[1]
      ? src.split('&file_extension=')[1]
      : ''

  const handleDocDownload = async (src: string) => {
    const link = document.createElement('a')
    link.href = src.replace('True', 'False')
    link.download = Date.now().toString()
    link.click()
  }

  const downloadableExtentionist = ['tif', 'tiff', 'doc', 'docx']
  const imageExtentionist = ['png', 'jpg', 'jpeg']

  const tiffExtentionist = ['tif', 'tiff']
  const docsExtentionist = ['doc', 'docx']
  const extraImagesExtentionist = ['webp', 'svg']

  const FileView = () => {
    return (
      <>
        {src && file_extension && imageExtentionist.includes(file_extension) ? (
          <ChakraImage src={src} objectFit={'cover'} className='embded' />
        ) : src && file_extension && tiffExtentionist.includes(file_extension) ? (
          <Text fontSize={'md'} paddingBottom={10}>
            Preview not available.
          </Text>
        ) : // <embed
        //   src={src}
        //   style={{
        //     maxHeight: '100%',
        //     objectFit: 'cover',
        //   }}
        //   type='image/tiff'
        //   />

        src && file_extension && docsExtentionist.includes(file_extension) ? (
          <Text fontSize={'md'} paddingBottom={10}>
            Preview not available.
          </Text>
        ) : // <embed
        //   src={src}
        //   style={{
        //     maxHeight: '100%',
        //     objectFit: 'cover',
        //   }}
        //   type='application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        // />
        src && file_extension && extraImagesExtentionist.includes(file_extension) ? (
          <embed
            src={src}
            style={{
              maxHeight: '100%',
              objectFit: 'cover',
            }}
            type='image/webp'
          />
        ) : file_extension && file_extension === 'pdf' ? (
          <embed
            src={src}
            style={{
              maxHeight: '100%',
              objectFit: 'cover',
              width: '100%',
              height: '100%',
            }}
            type='application/pdf'
          />
        ) : (
          <ChakraImage
            src={src}
            alt=''
            style={{
              maxHeight: '100%',
            }}
          />
        )}
      </>
    )
  }
  return (
    <>
      {src ? (
        <>
          <Flex
            direction='column'
            alignItems='center'
            justifyContent={'center'}
            border='1px'
            borderStyle='dashed'
            borderColor='grey'
            borderRadius='lg'
            maxW='140'
            height='130'
            padding='2'
            minWidth={full ? '100%' : '200px'}
            position='relative'
            overflow={'hidden'}
            mr={full ? '0' : '2'}
          >
            <FileView />
            <Flex
              position={'absolute'}
              flexDirection='column'
              justifyContent={'center'}
              alignItems={'center'}
              inset={0}
              background='#000000a6'
              color={'white'}
              zIndex={1}
            >
              {src && file_extension && downloadableExtentionist.includes(file_extension) ? (
                <Flex onClick={() => handleDocDownload(src)} cursor={'pointer'}>
                  <DownloadIcon />
                  <Text fontSize={'sm'} marginLeft={'5px'}>
                    Download
                  </Text>
                </Flex>
              ) : (
                <Flex onClick={onOpen} cursor={'pointer'}>
                  <ViewIcon />
                  <Text fontSize={'sm'} marginLeft={'5px'}>
                    View Fetched Details
                  </Text>
                </Flex>
              )}
            </Flex>
            <Flex position={'absolute'} top={1} right={1} zIndex={2}>
              <MdCancel
                cursor={'pointer'}
                onClick={onClear}
                color='red'
                style={{
                  background: 'white',
                  borderRadius: '50%',
                }}
              />
            </Flex>
          </Flex>

          <Modal isOpen={isOpen} onClose={onClose} size='5xl' isCentered>
            <ModalOverlay backdropFilter={'blur(2px)'} />
            <ModalContent
              backgroundColor={'transparent'}
              shadow={'none'}
              rounded={5}
              height={'90%'}
              justifyContent={'center'}
            >
              <ModalCloseButton>
                <MdCancel
                  color='red'
                  size={'25px'}
                  style={{
                    background: 'white',
                    borderRadius: '50%',
                  }}
                />
              </ModalCloseButton>
              <ModalBody>
                <Flex
                  justifyContent={'center'}
                  alignItems={'center'}
                  objectFit={'cover'}
                  backgroundColor={'transparent'}
                  height={'100%'}
                >
                  <FileView />
                </Flex>
              </ModalBody>
            </ModalContent>
          </Modal>
        </>
      ) : (
        <Flex
          direction='column'
          alignItems='center'
          border='1px'
          borderStyle='dashed'
          borderColor='grey'
          borderRadius='lg'
          maxW='250'
          height={'130'}
          padding='2'
          {...getRootProps()}
          aria-label='drag and drop area'
          minWidth={'100%'}
        >
          <Icon marginTop='3' w='6' h='6' as={BsFileEarmarkBinaryFill} />
          <Text textAlign='center' padding='2' fontSize='sm' color='grey'>
            {uploadDesc}
          </Text>
          <input
            {...getInputProps()}
            capture={onlyCameraAllowed === true ? 'environment' : undefined}
          />

          <ElevatedButton isLoading={isLoading} size='sm' marginBottom='3'>
            UPLOAD FILE
          </ElevatedButton>
        </Flex>
      )}
    </>
  )
}
